import React from "react";

import { styled } from "@linaria/react";
import * as io from "io-ts";
import createGlobalState from "@ui/lib/createGlobalState";
import { useModals } from "@ui/lib/hooks/useModal";
import t from "../../../../i18n";
import { color, fonts } from "@ui";
import SecondaryHeader from "@ui/Header/SecondaryHeader";
import Modal from "@ui/Modal";
import SidePanelLayout from "@ui/SidePanel/Layout";
import TableCard from "@ui/Table/Card";
import { type Project } from "../../../../api/useProjects";
import { useQuantities } from "../../../../api/useQuantities";
import { useCanUserDoOnProject } from "../../../canUserDo";
import SearchInputV2 from "@ui/Input/SearchInputV2";
import { LoadedFileName } from "../LoadedFileName";
import { TableCardWithoutBorder } from "@ui/Table/_styles/TableCardWithoutBorder";
import Button from "@ui/Button";
import { DeleteBinLine, DownloadLine, More2Fill, UploadLine } from "@ui/IconV2/react";
import Option, { OptionGroup } from "@ui/Option";
import Menu from "@ui/Menu";
import ButtonIconV2 from "@ui/ButtonIconV2";
import { useGet } from "../../../../api/useCall";
import download from "downloadjs";
import { generateExportFileName } from "diagnostics-lib/string";
import { css } from "@linaria/core";
import { useOutletContext } from "react-router";
import { useDisplayFilters, type DisplayFilter } from "../../../../api/useDisplayFilters";
import DisplayFiltersModalForm from "./ModalForm/DisplayFiltersModalForm";
import DeleteDisplayFiltersModalForm from "./ModalForm/DeleteDisplayFiltersModalForm";
import Breadcrumb from "../BreadcrumbProject";
import createIOSortType from "../../../../lib/createIOSortType";
import useFilteredDatas from "@ui/lib/hooks/useFilteredDatas";
import type { SortableValue } from "diagnostics-lib/comparator";
import useSortedData from "../../../../lib/hooks/useSortedData";
import type { ColumnMetaData } from "@ui/Table/ColumnMetaData";
import Table from "@ui/Table";
import { Tag } from "@ui/Tag";

const useFiltersSearch = createGlobalState({
  storage: "session",
  key: "displayFiltersSearch",
  type: io.string,
  defaultValue: "",
  scoped: true,
});

const columns = ["group", "name", "quantity", "min", "max", "maxStd"] as const;
type Column = (typeof columns)[number];

const useSortInput = createGlobalState({
  storage: "session",
  key: "displayFiltersSort",
  type: createIOSortType(columns),
  defaultValue: {
    column: "name",
    direction: "asc",
  },
  scoped: true,
});

type DisplayFilterInput = {
  name: string;
  group: string | null;
  isDefault: boolean | null;
  quantity: string;
  min: number | null;
  max: number | null;
  maxStd: number | null;
};
const sortSelector: { [key in Column]: (b: DisplayFilterInput) => SortableValue | null } = {
  name: b => b.name,
  group: b => b.group,
  quantity: b => b.quantity,
  min: b => b.min,
  max: b => b.max,
  maxStd: b => b.maxStd,
};
const computeInputs = (displayFilters: DisplayFilter[]) =>
  displayFilters.map(displayFilter => ({
    name: displayFilter.name,
    group: displayFilter.group,
    isDefault: displayFilter.isDefault,
    quantity: displayFilter.quantity.name,
    min: displayFilter.min,
    max: displayFilter.max,
    maxStd: displayFilter.maxStd,
  }));

export const DisplayFilters = () => {
  const project = useOutletContext<Project>();
  if (!project) throw new Error("no project");

  const exportDisplayFilters = useGet(
    "/projects/:projectId(uuid)/quantities/displayFilters/export",
  );
  const quantities = useQuantities(project.id);
  const displayFilters = useDisplayFilters(project.id);
  const indexByQuantityName = React.useMemo(() => {
    const index: { [key: string]: (typeof displayFilters)[number]["quantity"] } = {};
    for (const q of quantities) {
      index[q.name] = {
        id: q.id,
        name: q.name,
        unit: q.unit,
      };
    }
    return index;
  }, [quantities]);

  const displayFilterInputs = React.useMemo(() => computeInputs(displayFilters), [displayFilters]);

  const [search, setSearch] = useFiltersSearch({ scope: project.id });
  const [sort, setSort] = useSortInput({ scope: project.id });

  const [displayFilterData] = useFilteredDatas(displayFilterInputs, search, input => input.name);
  const sortedDisplayFilters = useSortedData(
    displayFilterData,
    sortSelector[sort.column],
    sort.direction,
  );

  const { modal, closeModal, openModal } = useModals<"delete" | "replace">();
  const canEditSetup = useCanUserDoOnProject("PROJECT_SETUP", project.id);

  const handleExport = React.useCallback(async () => {
    const res = await exportDisplayFilters({ routeParams: { projectId: project.id } });

    download(
      res.body as unknown as Blob,
      generateExportFileName({
        filename: "display_filters",
        projectName: project.name.toLocaleLowerCase(),
        extension: "csv",
      }),
    );
  }, [exportDisplayFilters, project.name, project.id]);

  const handleSearchChange = React.useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setSearch(e.currentTarget.value);
    },
    [setSearch],
  );

  const metadata: { [key in Column]: ColumnMetaData<DisplayFilterInput> } = {
    group: {
      label: t("settings.display_filter.table.column.group"),
      render: (b: DisplayFilterInput) => b.group,
      minWidth: "200px",
      sortable: true,
    },
    name: {
      label: t("settings.display_filter.table.column.name"),
      render: (b: DisplayFilterInput) => (
        <div className={filterNameCellCss}>
          <span>{b.name}</span>
          {b.isDefault ? (
            <StyledTag size="s">
              {t("project.settings.display_filters.groups.default_badge")}
            </StyledTag>
          ) : null}
        </div>
      ),
      minWidth: "200px",
      sortable: true,
    },
    quantity: {
      label: t("settings.display_filter.table.column.quantity"),
      render: (b: DisplayFilterInput) => b.quantity,
      minWidth: "200px",
      sortable: true,
    },
    min: {
      label: t("quantities.crossFilter.table.min"),
      render: (b: DisplayFilterInput) =>
        b.min !== null && indexByQuantityName[b.quantity]?.unit
          ? b.min + " " + indexByQuantityName[b.quantity]?.unit
          : b.min,
      minWidth: "100px",
      sortable: true,
    },
    max: {
      label: t("quantities.crossFilter.table.max"),
      render: (b: DisplayFilterInput) =>
        b.max !== null && indexByQuantityName[b.quantity]?.unit
          ? b.max + " " + indexByQuantityName[b.quantity]?.unit
          : b.max,
      minWidth: "100px",
      sortable: true,
    },
    maxStd: {
      label: t("quantities.crossFilter.table.maxStd"),
      render: (b: DisplayFilterInput) =>
        b.maxStd !== null && indexByQuantityName[b.quantity]?.unit
          ? b.maxStd + " " + indexByQuantityName[b.quantity]?.unit
          : b.maxStd,
      minWidth: "100px",
      sortable: true,
    },
  };

  const headerLeft = (
    <SearchInputV2
      data-cy="search-filter"
      placeholder={t("commons.placeholder.search")}
      onChange={handleSearchChange}
      value={search}
    />
  );
  const headerRight = (
    <div className={ContainerHeaderRight}>
      {canEditSetup ? (
        <Button variant="primary" onClick={() => openModal("replace")} leftIcon={<UploadLine />}>
          {t("commons.message_card.replace")}
        </Button>
      ) : null}

      {canEditSetup ? (
        <StyledMenu
          arrowWidth={0}
          placement="bottom-end"
          overlay={
            <>
              <OptionGroup>
                <Option closeOnClick onClick={handleExport} disabled={!canEditSetup}>
                  <DownloadLine color={color.carbon800} />
                  {t("commons.message_card.export")}
                </Option>
                <Option closeOnClick onClick={() => openModal("delete")} disabled={!canEditSetup}>
                  <DeleteBinLine color={color.carbon800} />
                  {t("commons.message_card.delete")}
                </Option>
              </OptionGroup>
            </>
          }
        >
          <ButtonIconV2>
            <More2Fill size="m" />
          </ButtonIconV2>
        </StyledMenu>
      ) : null}
    </div>
  );

  return (
    <>
      <SecondaryHeader
        breadcrumb={
          <Breadcrumb
            project={project}
            pathName={t("project.secondary_header.display_filters.title")}
          />
        }
      />

      <StyledSidePanelLayout id="displayFilters" title="">
        <LoadedFileName fileName={project.displayFiltersFileName} />
        <StyledTableContainer>
          <TableCard
            title=""
            headerLeft={headerLeft}
            headerRight={headerRight}
            className={TableCardWithoutBorder}
          >
            <Table
              stickyHeader
              datas={sortedDisplayFilters}
              metadata={metadata}
              computeRowKey={(displayFilter: DisplayFilterInput) => displayFilter.name}
              sort={sort}
              onSort={setSort}
            />
          </TableCard>
        </StyledTableContainer>
      </StyledSidePanelLayout>

      <Modal
        title={t("project.settings.display_filters.replace.modal.title")}
        subtitle={t("project.settings.display_filters.replace.modal.subtitle")}
        isOpen={modal === "replace"}
        onRequestClose={closeModal}
      >
        <DisplayFiltersModalForm
          projectId={project.id}
          onRequestClose={closeModal}
          needRedirect={false}
        />
      </Modal>

      <Modal
        title={t("project.settings.analyses.delete_display_filters.modal.title")}
        isOpen={modal === "delete"}
        onRequestClose={closeModal}
      >
        <DeleteDisplayFiltersModalForm onRequestClose={closeModal} projectId={project.id} />
      </Modal>
    </>
  );
};

export default React.memo(DisplayFilters);

//#region CSS
const ContainerHeaderRight = css`
  display: flex;
  gap: 8px;
`;

const filterNameCellCss = css`
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;

  > :first-child {
    min-width: 0;
  }
`;
const StyledTag = styled(Tag)`
  margin-left: auto;
  width: fit-content;
  flex-shrink: 0;
`;
const StyledSidePanelLayout = styled(SidePanelLayout)`
  gap: 0px;
`;

const StyledMenu = styled(Menu)`
  font: ${fonts.body2};
`;

const StyledTableContainer = styled.div`
  .EmptyCard {
    flex: none;
  }
`;
//#endregion CSS
